/*
 * Tune - the jetting advisor.
 *
 * Answers one question at the top of the day: what main jet is today's air
 * asking for? Current conditions come from the nearest BOM station for the
 * location you pick (a stopgap until the van's own weather unit is fitted); the
 * recommendation is drawn from THIS engine's own history in THIS class,
 * nearest-neighbour on air density - and made for TODAY's carb, because a jet
 * at a different float height or with a return line is a different map.
 *
 * It is deliberately honest about thin data. Below a handful of comparable
 * runs it shows the air and the history but withholds a number, because a
 * confident jet pulled from three noisy sessions is worse than deciding by feel.
 */

import type { ReactNode } from 'react'
import { useState } from 'react'
import { keepPreviousData, useQuery } from '@tanstack/react-query'
import { Wind, Gauge, ThermometerSun, Droplets, Info, MapPin, Sliders } from 'lucide-react'
import { api } from '../lib/api'
import type { TuneResult } from '../lib/api'
import { Empty, Spinner } from '../components/bits'
import { lapTime } from '../lib/format'

function Stat({ icon, label, value, unit }: {
  icon: ReactNode; label: string; value: string | number | null; unit?: string
}) {
  return (
    <div className="card" style={{ padding: 12, flex: '1 1 120px' }}>
      <div className="row small muted" style={{ gap: 6, marginBottom: 4 }}>
        {icon}{label}
      </div>
      <div style={{ fontSize: 22, fontWeight: 600 }}>
        {value === null || value === undefined ? '—' : value}
        {unit && value !== null && value !== undefined
          ? <span className="muted" style={{ fontSize: 13, marginLeft: 3 }}>{unit}</span> : null}
      </div>
    </div>
  )
}

const CONF: Record<string, { text: string; color: string }> = {
  none: { text: 'not enough history', color: 'var(--muted)' },
  low: { text: 'low confidence', color: 'var(--amber)' },
  moderate: { text: 'moderate confidence', color: 'var(--green)' },
}

export function Tune() {
  // null = follow the backend default (latest session's track / current carb).
  const [loc, setLoc] = useState<string | null>(null)
  const [floatOverride, setFloatOverride] = useState<string | null>(null)
  const [returnOverride, setReturnOverride] = useState<string | null>(null)

  const params = new URLSearchParams()
  if (loc) params.set('track', loc)
  if (floatOverride) params.set('float_height', floatOverride)
  if (returnOverride) params.set('return_line', returnOverride)
  const qs = params.toString()

  const q = useQuery({
    queryKey: ['tune', loc, floatOverride, returnOverride],
    queryFn: () => api.get<TuneResult>(`/api/tune${qs ? `?${qs}` : ''}`),
    refetchOnWindowFocus: false,
    placeholderData: keepPreviousData,
  })

  if (q.isLoading) return <div className="page"><Spinner /></div>
  if (q.isError || !q.data) {
    return (
      <div className="page">
        <div className="page-head"><div><h1>Tune</h1></div></div>
        <Empty title="Couldn't load the tune advisor"
          body="The jetting history or weather feed didn't come back. Try again in a moment." />
      </div>
    )
  }

  const d = q.data
  const c = d.conditions
  const rec = d.recommendation
  const conf = rec ? (CONF[rec.confidence] ?? CONF.none) : CONF.none

  // Displayed control values: the override if the user set one, otherwise the
  // carb that's actually on the kart (from the latest setup).
  const floatVal = floatOverride ?? (d.carb.float_height_mm != null ? String(d.carb.float_height_mm) : '')
  const returnVal = returnOverride ?? (d.carb.return_line || '')
  const selectedLoc = loc ?? d.selected_location ?? ''

  return (
    <div className="page">
      <div className="page-head">
        <div>
          <h1>Tune</h1>
          <div className="sub">
            {d.class} · what today's air is asking for
            {c?.station ? <> · station <b>{c.station}</b></> : null}
          </div>
        </div>
      </div>

      {/* Where you are + the carb the advice is made for ---------------- */}
      <div className="card" style={{ marginBottom: 12 }}>
        <div className="grid" style={{ gap: 12, gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))' }}>
          <div>
            <label className="tiny dim"><MapPin size={12} style={{ verticalAlign: -1 }} /> Where are you today?</label>
            <select value={selectedLoc} style={{ width: '100%' }}
              onChange={(e) => setLoc(e.target.value)}>
              {d.locations.map((l) => (
                <option key={l.slug} value={l.slug}>
                  {l.name}{l.mapped ? '' : ' — no station yet'}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="tiny dim"><Sliders size={12} style={{ verticalAlign: -1 }} /> Float / lever height (mm)</label>
            <input type="number" step="0.1" value={floatVal} placeholder="not recorded"
              style={{ width: '100%' }}
              onChange={(e) => setFloatOverride(e.target.value)} />
          </div>
          <div>
            <label className="tiny dim">Return fuel line?</label>
            <select value={returnVal} style={{ width: '100%' }}
              onChange={(e) => setReturnOverride(e.target.value)}>
              <option value="">not recorded</option>
              <option value="yes">yes — return line fitted</option>
              <option value="no">no return line</option>
            </select>
          </div>
        </div>
        <div className="tiny muted" style={{ marginTop: 8, lineHeight: 1.5 }}>
          The jet is worked out for this exact carb. Change the float height or the
          return line and it re-checks against only the sessions that ran the same
          way — a jet from a different float height or fuel system doesn't carry across.
        </div>
      </div>

      {/* Current air ---------------------------------------------------- */}
      {c ? (
        <>
          <div className="row" style={{ gap: 10, flexWrap: 'wrap', marginBottom: 12 }}>
            <Stat icon={<Wind size={15} />} label="Air density (RAD)" value={c.rad} />
            <Stat icon={<ThermometerSun size={15} />} label="Air temp" value={c.air_temp_c} unit="°C" />
            <Stat icon={<Droplets size={15} />} label="Humidity" value={c.humidity_pct} unit="%" />
            <Stat icon={<Gauge size={15} />} label="Pressure" value={c.pressure_hpa} unit="hPa" />
          </div>
          {c.label ? (
            <div className="card" style={{ marginBottom: 14 }}>
              <div className="row small" style={{ gap: 8 }}>
                <Info size={16} style={{ opacity: .6, flexShrink: 0 }} />
                <span>{c.label}{c.observed_at ? <span className="muted"> · observed {c.observed_at}</span> : null}</span>
              </div>
            </div>
          ) : null}
        </>
      ) : (
        <div className="card" style={{ marginBottom: 14 }}>
          <div className="row small muted" style={{ gap: 8 }}>
            <Info size={16} style={{ flexShrink: 0 }} />
            No Bureau station is mapped for this track yet, so there's no live air
            reading — enter the jet by feel and it'll still be recorded for next time.
          </div>
        </div>
      )}

      {/* The recommendation -------------------------------------------- */}
      <div className="card" style={{ marginBottom: 14 }}>
        <div className="between" style={{ alignItems: 'flex-start' }}>
          <div>
            <div className="small muted" style={{ marginBottom: 4 }}>Suggested main jet</div>
            {rec?.recommended != null ? (
              <div style={{ fontSize: 40, fontWeight: 700, lineHeight: 1 }}>{rec.recommended}</div>
            ) : (
              <div style={{ fontSize: 24, fontWeight: 600, color: 'var(--muted)' }}>—</div>
            )}
            <div className="small" style={{ color: conf.color, marginTop: 6 }}>{conf.text}</div>
          </div>
          {d.current_jet ? (
            <div style={{ textAlign: 'right' }}>
              <div className="small muted">currently on</div>
              <div style={{ fontSize: 24, fontWeight: 600 }}>{d.current_jet.main_jet}</div>
              <div className="tiny muted">clip {d.current_jet.needle_clip || '—'} · {d.current_jet.from}</div>
            </div>
          ) : null}
        </div>
        {rec?.reason ? (
          <div className="small muted" style={{ marginTop: 10, lineHeight: 1.5 }}>{rec.reason}</div>
        ) : null}
      </div>

      {/* The history it learned from ----------------------------------- */}
      <div className="card">
        <div className="between" style={{ marginBottom: 8 }}>
          <b>Jetting history</b>
          <span className="small muted">{d.history_count} run{d.history_count === 1 ? '' : 's'} in {d.class}</span>
        </div>
        {d.history.length === 0 ? (
          <div className="small muted" style={{ lineHeight: 1.5 }}>
            Nothing recorded yet. Every setup you save now captures the air it ran
            in — after a handful of sessions this becomes a real chart.
          </div>
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <table className="table" style={{ width: '100%', fontSize: 13 }}>
              <thead>
                <tr>
                  <th style={{ textAlign: 'left' }}>Session</th>
                  <th>Jet</th><th>Clip</th><th>RAD</th><th>Air °C</th><th>Best lap</th>
                </tr>
              </thead>
              <tbody>
                {d.history.map((h) => {
                  const near = c?.rad != null && Math.abs(h.ratio - c.rad) <= 0.01
                  return (
                    <tr key={h.session_id} style={near ? { background: 'var(--hover)' } : undefined}>
                      <td style={{ textAlign: 'left' }}>{h.label}</td>
                      <td style={{ textAlign: 'center', fontWeight: near ? 700 : 400 }}>{h.main_jet}</td>
                      <td style={{ textAlign: 'center' }}>{h.needle_clip || '—'}</td>
                      <td style={{ textAlign: 'center' }}>{h.ratio.toFixed(3)}</td>
                      <td style={{ textAlign: 'center' }}>{h.air_temp_c ?? '—'}</td>
                      <td style={{ textAlign: 'center' }}>{lapTime(h.best_lap_s)}</td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
            {c?.rad != null ? (
              <div className="tiny muted" style={{ marginTop: 6 }}>
                Rows near today's air (RAD {c.rad}) are highlighted — those are the ones the suggestion leans on.
              </div>
            ) : null}
          </div>
        )}
      </div>
    </div>
  )
}
